import axios from 'axios'
import parseXml from './parse-xml'
import Timer from "./timer"


/**
 * Class for doing ajax requests
 *
 * This is a wrapper axios ajax library
 *
 * It adds retry functionality that can handle loss of network connection and
 * has better error handling than the standard fetch API.
 *
 * Usage:import *       var Ajax from './ajax'

 *
 *       Ajax.get({
 *       url: "http://localhost/userdata.json",
 *       responseType: 'JSON'
 *        retry: {
 *          times: 5,
 *           waitTimeBetweenRetries: 1000,
 *            statusCodes: [500]
 *           }
 *      }).then (function (json) {
 *         alert("UserId: " + json.userId);
 *      }).catch(function (error) {
 *         console.error("something went wrong with the ajax request", error);
 *      });
 *
 * Options:
 *  - responseType (required)
 *    This can be 'XML', 'JSON', 'TEXT', 'IMAGE', 'BLOB'
 *    If you specify XML you will get an XML document as result
 *    If you specify JSON you will get an JSON object as result
 *    If you specify TEXT you will get a plain text string as result
 *    If you specify IMAGE you will get an object url to an image as a result (that you can put in img src)
 *    If you specify BLOB you will get a Blob object
 *    If it cannot give you what you asked, you will get an error message instead
 *  - retry (optional)
 *    An object that specifies when to retry failed requests. It has the following possible parameters:
 *    - times (how many times to you want it to retry before giving up?)
 *    - waitTimeBetweenRetries (how many milliseconds to wait before trying again)
 *    - statusCodes (an array of status codes to trigger a retry)
 *    - regexMatchResponse (a regex to match a response that will trigger a retry)
 *  - headers (optional)
 *    An object with key:value pairs where you specify additional headers for the request
 *  - body (for POST and PUT requests)
 *    This can be any of the following:
 *    - string, plain object, ArrayBuffer, ArrayBufferView, URLSearchParams, FormData, File, Blob
 */

var Ajax = {};


Ajax.get = function (options) {
    Ajax._validateOptions(options, 'GET');
    return new Promise(function (resolve, reject) {
        Ajax._fetch(options.url, 'GET', options, resolve, reject, 0);
    });
};

Ajax.post = function (options) {
    Ajax._validateOptions(options, 'POST');
    return new Promise(function (resolve, reject) {
        Ajax._fetch(options.url, 'POST', options, resolve, reject, 0);
    });
};

Ajax.put = function (options) {
    Ajax._validateOptions(options, 'PUT');
    return new Promise(function (resolve, reject) {
        Ajax._fetch(options.url, 'PUT', options, resolve, reject, 0);
    });
};

Ajax.delete = function (options) {
    Ajax._validateOptions(options, 'DELETE');
    return new Promise(function (resolve, reject) {
        Ajax._fetch(options.url, 'DELETE', options, resolve, reject, 0);
    });
};


/*** PRIVATE METHODS ***/
Ajax._fetch = function (url, method, options, resolve, reject, retryCount) {

    var requestOptions = {
        method: method,
        url: url
    };
    if (options.body) {
        requestOptions.data = options.body;
    }
    if (options.headers) {
        requestOptions.headers = options.headers;
    }
    if (options.responseType === 'IMAGE') {
        requestOptions.responseType = 'blob';
    }
    if (typeof options.timeout === "number") {
        requestOptions.timeout = options.timeout;
    }

    requestOptions.validateStatus = function (status) {
        return status >= 200; //Most statuses will not throw axios error, but will be validated by this class instead
    };

    axios(requestOptions).then(function (response) {
        Ajax._validateResponse(response, options);
        Ajax._handleResponseData(response, options, resolve);
    }).catch(function (error) {

        var retry = false;

        if (options.retry) {
            retry = retryCount < options.retry.times;
        }

        var timeout = 1000;
        // check if the retry count didn't exceed the allowed number
        if (options.retry && options.retry.waitTimeBetweenRetries) {
            timeout = options.retry.waitTimeBetweenRetries;
        }
        //if network errors, try again
        if ((error.response && !error.response.status) || (error.message && error.message.indexOf("Network Error") > -1)
            || (error.code && error.code === 'ECONNABORTED')) {
            if (console) {
                console.error(error);
                console.error("Unable to fetch data from remote API (%s), perhaps browser is offline or there is CORS errors, trying again. If CORS errors, this needs to be handled on the server by adding headers such as Access-Control-Allow-Origin. Sometimes the server has these in their 200 OK responses, but not in their 500 or 400 responses, which also needs to be fixed on the server or else the real error is hidden. Error message:", url, error.message);
            }
            // if the error is due to dropped internet connection, we will always do retry
            retry = true;
            // but to avoid spamming with request, we extend 1 second to 5
            timeout = 5000;
        }

        if (retry) {

            // timer settings didn't work. need to test more why
            setTimeout(function() {
                Ajax._fetch(url, method, options, resolve, reject, retryCount + 1);
            }, timeout);

            //Set a timeout to try this request again
            // Timer.set( "Ajax" + url, function () {
            //     Ajax._fetch(url, method, options, resolve, reject, retryCount + 1);
            // }, timeout);

        } else {

            var status = null;
            var responseText = null;
            var message = null;
            if (error.response) {
                if (error.response.status) {
                    status = error.response.status;
                }
                if (error.response.data) {
                    responseText = error.response.data;
                }
            }
            if (error.message) {
                message = error.message;
            }

            var errorData = {
                message: "Unable to complete ajax request",
                subMessage: message,
                url: url,
                method: method,
                options: JSON.stringify(options),
                status: status,
                error: JSON.stringify(error),
                retryCount: retryCount,
                responseText: responseText
            };
            reject(errorData);
        }
    });
};

/**
 * Validates that the given options is correct
 * Throws an error if not.
 * @param  {Object}
 * @param  {String}
 */
Ajax._validateOptions = function (options, method) {
    var validResponseTypes = ['XML', 'JSON', 'TEXT', 'IMAGE', 'BLOB'];
    if (!options.hasOwnProperty("responseType")) {
        throw new Error("responseType needs to be specified in the options object when using the Ajax lib. And it needs to be one of the following: " + validResponseTypes.join(','));
    }
    if (validResponseTypes.indexOf(options.responseType) < 0) {
        throw new Error("responseType needs to be one of " + validResponseTypes.join(','));
    }

    if (method === 'POST' || method === 'PUT') {
        if (!options.hasOwnProperty("body")) {
            throw new Error("POST and PUT requests must specify the body parameter in the options object");
        }
    }
};

/**
 * Validate response status code and other validations
 * Throws an error if there's anything wrong
 *
 * @param {Response} response
 * @param {string|Object} options
 */
Ajax._validateResponse = function (response, options) {
    //Check if there is a regex to check if the response is invalid and we should try again
    if (options.retry && options.retry.regexMatchResponse && response.request.responseText.match &&
        response.request.responseText.match(options.retry.regexMatchResponse)) {
        throw {
            message: "Response matched regex to try again",
            response: response,
            responseText: response.request.responseText,
            retry: true
        };
    }

    //Check if status code is ok
    if (response.status) {
        if (response.status > 399) {
            if (options.retry) {
                if (options.retry.statusCodes && options.retry.statusCodes.indexOf(response.status) >= 0) {
                    throw {
                        message: "Unsuccessful ajax request. Got answer back from server but it had status code of: " + response.status,
                        response: response,
                        retry: true
                    };
                }
            }
            throw {
                message: "Unsuccessful ajax request. Got answer back from server but it had status code of: " + response.status,
                response: response,
                retry: false
            };
        }
    } else {
        throw {
            message: "Unsuccessful ajax request. Got answer back from server but it had no status code",
            response: response,
            retry: true
        };
    }
};

/**
 * Parse the response body into JSON or XML or whatever the user asked for
 * @param {string|Object} response
 * @param {Object} options
 * @param {Function} resolve
 */
Ajax._handleResponseData = function (response, options, resolve) {
    if (options.responseType === 'JSON') {
        resolve(response.data);
    } else if (options.responseType === 'XML') {
        if (!parseXml) {
            throw {
                message: "Unable to parse responseText into XML object because the parseXML lib could not be loaded",
                response: response,
                responseText: response.data
            };
        }
        try {
            var parsedXml = parseXml(response.data);
        } catch (error) {
            throw {
                message: "Unable to parse responseText into XML object",
                response: response,
                responseText: response.data,
                parseError: error
            };
        }

        //Check if the parsedXml contains error messages instead of the parsed xml
        if (parsedXml.getElementsByTagName('parsererror').length > 0) {
            throw {
                message: "Unable to parse responseText into XML object",
                response: response,
                responseText: response.data,
                parseError: (new XMLSerializer()).serializeToString(parsedXml.getElementsByTagName('parsererror')[0])
            };
        }

        resolve(parsedXml);
    } else if (options.responseType === 'IMAGE') {
        try {
            var url = URL.createObjectURL(response.data);
        } catch (error) {
            throw {
                message: "Unable to convert response into an image ObjectUrl",
                response: response,
                responseText: response.data,
                parseError: error
            };
        }
        resolve(url);
    } else if (options.responseType === 'BLOB') {
        resolve(response.data);
    } else if (options.responseType === 'TEXT') {
        resolve(response.data);
    }
};

export default Ajax;
